import React, { useState, useEffect, useRef } from "react";

import "./PropertiesEngine.css";
import PropertyCard from "../components/PropertyCard";
import propertiesData from "./Properties.json"; // Import JSON file
import { ArrowDropDown, Search } from "@mui/icons-material";

function PropertiesEngine() {
  const [properties, setProperties] = useState(propertiesData); // Initialize with JSON data
  const [selectedTags, setSelectedTags] = useState([]); // For filtering by tags
  const [sortOrder, setSortOrder] = useState(""); // Sort order state
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false); // Sort dropdown open state
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false); // Category dropdown open state

  const sortDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);

  // Toggle dropdown visibility
  const toggleSortDropdown = () => setIsSortDropdownOpen((prev) => !prev);

  const toggleCategoryDropdown = () =>
    setIsCategoryDropdownOpen((prev) => !prev);

  // Close dropdowns if clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(event.target)
      ) {
        setIsSortDropdownOpen(false);
      }
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target)
      ) {
        setIsCategoryDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle sorting by price
  const handleSort = (order) => {
    setSortOrder(order);
    setIsSortDropdownOpen(false);
    const sorted = [...properties].sort((a, b) => {
      const priceA = parseInt(a.price.replace(/[$,]/g, ""));
      const priceB = parseInt(b.price.replace(/[$,]/g, ""));
      return order === "asc" ? priceA - priceB : priceB - priceA;
    });
    setProperties(sorted);
  };

  // Reset filters and sorting
  const resetFilters = () => {
    setSelectedTags([]);
    setSortOrder("");
    setProperties(propertiesData);
  };

  // Toggle tag filter
  const toggleTag = (tag) => {
    let updatedTags;
    if (selectedTags.includes(tag)) {
      updatedTags = selectedTags.filter((t) => t !== tag);
    } else {
      updatedTags = [...selectedTags, tag];
    }

    setSelectedTags(updatedTags);

    if (updatedTags.length === 0) {
      setProperties(propertiesData);
    } else {
      const filtered = propertiesData.filter((property) =>
        updatedTags.includes(property.tag)
      );
      setProperties(filtered);
    }
  };

  return (
    <div className="PropertiesEngine">
      <div className="PropertiesEngine__topblocks">
        <div className="PropertiesEngine__topblocks__left">
          <div className="PropertiesEngine__topblocks__left__icon">
            <Search />
          </div>
          <p>{properties.length} Exceptional properties </p>
        </div>
        <div className="PropertiesEngine__topblocks__right">
          <div className="PropertiesEngine__filters">
            <div className="custom-dropdown" ref={sortDropdownRef}>
              <button
                className="PropertiesEngine__dropdownbutton"
                onClick={toggleSortDropdown}
              >
                {sortOrder === "asc"
                  ? "Lowest"
                  : sortOrder === "desc"
                  ? "Highest"
                  : "Price"}
                <div
                  className={`PropertiesEngine__dropdownbutton__arrow ${
                    isSortDropdownOpen ? "open" : ""
                  }`}
                >
                  <ArrowDropDown />
                </div>
              </button>
              {isSortDropdownOpen && (
                <div className="PropertiesEngine__dropdownmenu__price">
                  <div
                    className="PropertiesEngine__dropdownitem"
                    onClick={() => handleSort("asc")}
                  >
                    Low to High
                  </div>
                  <div
                    className="PropertiesEngine__dropdownitem"
                    onClick={() => handleSort("desc")}
                  >
                    High to Low
                  </div>
                </div>
              )}
            </div>{" "}
            <div className="custom-dropdown" ref={categoryDropdownRef}>
              <button
                className="PropertiesEngine__dropdownbutton"
                onClick={toggleCategoryDropdown}
              >
                Category
                <div
                  className={`PropertiesEngine__dropdownbutton__arrow ${
                    isCategoryDropdownOpen ? "open" : ""
                  }`}
                >
                  <ArrowDropDown />
                </div>
              </button>
              {isCategoryDropdownOpen && (
                <div className="PropertiesEngine__dropdownmenu__category">
                  <label className="PropertiesEngine__dropdownitem">
                    <input
                      type="checkbox"
                      onChange={() => toggleTag("rustic")}
                      checked={selectedTags.includes("rustic")}
                      className="categorydisplay"
                    />
                    Rustic
                  </label>
                  <label className="PropertiesEngine__dropdownitem">
                    <input
                      type="checkbox"
                      onChange={() => toggleTag("contemporary")}
                      checked={selectedTags.includes("contemporary")}
                      className="categorydisplay"
                    />
                    Contemporary
                  </label>
                  <label className="PropertiesEngine__dropdownitem">
                    <input
                      type="checkbox"
                      onChange={() => toggleTag("resale")}
                      checked={selectedTags.includes("resale")}
                      className="categorydisplay"
                    />
                    Resale
                  </label>
                  <label className="PropertiesEngine__dropdownitem">
                    <input
                      type="checkbox"
                      onChange={() => toggleTag("investment")}
                      checked={selectedTags.includes("investment")}
                      className="categorydisplay"
                    />
                    Investment
                  </label>
                </div>
              )}
            </div>
            <button onClick={resetFilters} className="PropertiesEngine__reset">
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Property cards */}
      <div className="PropertiesEngine__rows">
        {properties.map((property, index) => (
          <PropertyCard
            key={index}
            img={property.image}
            name={property.name}
            location={property.location}
            price={property.price}
            bedrooms={property.bedrooms}
            bathrooms={property.bathrooms}
            housesize={property.housesize}
            status={property.status}
            badgecolor={property.badgecolor}
            slug={property.slug}
          />
        ))}
      </div>
    </div>
  );
}

export default PropertiesEngine;
